import { useCallback, useEffect, useState } from "react";
import { fetchKlines } from "@helpers/futures/klines";
import { CANDLES_STATUS } from "../constants";
import { getTradeCandleRange } from "../utils/chartView";

const LOADING_STATE = {
  status: CANDLES_STATUS.LOADING,
  candles: [],
  entryIndex: 0,
  intervalMs: null,
};

/**
 * Market candles from the trade's open to its close, or to now for an open
 * trade.
 *
 * @param {{symbol: string, openedAt: number|null, closedAt: number|null}} trade
 * @param {boolean} isEnabled false when the trade is shown without a chart
 * @returns {{status: string, candles: object[], entryIndex: number, intervalMs: number|null, retry: () => void}}
 */
export const useTradeCandles = ({ symbol, openedAt, closedAt }, isEnabled) => {
  const [state, setState] = useState(LOADING_STATE);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!isEnabled || !symbol || openedAt == null) {
      setState({ ...LOADING_STATE, status: CANDLES_STATUS.EMPTY });
      return undefined;
    }

    let isCancelled = false;
    const endTime = closedAt ?? Date.now();
    const { interval, ms } = getTradeCandleRange(openedAt, endTime);

    setState(LOADING_STATE);

    fetchKlines({
      symbol,
      interval,
      // From the start of the candle the trade was opened in
      startTime: Math.floor(openedAt / ms) * ms,
      endTime,
    })
      .then((candles) => {
        if (isCancelled) return;

        if (!candles.length) {
          setState({ ...LOADING_STATE, status: CANDLES_STATUS.EMPTY });
          return;
        }

        // The candle the trade was opened in
        const entryIndex = candles.findIndex(
          (candle) => candle.time + ms > openedAt,
        );

        setState({
          status: CANDLES_STATUS.READY,
          candles,
          entryIndex: Math.max(0, entryIndex),
          intervalMs: ms,
        });
      })
      .catch(() => {
        if (!isCancelled) {
          setState({ ...LOADING_STATE, status: CANDLES_STATUS.ERROR });
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [isEnabled, symbol, openedAt, closedAt, attempt]);

  const retry = useCallback(() => setAttempt((value) => value + 1), []);

  return { ...state, retry };
};
